import { WideSelectOption } from 'components/_pages/booking/Form/WideSelect';
import { CustomSelectOption } from 'components/ui/Select';
import dayjs, { Dayjs } from 'dayjs';
import { DEFAULT_INTERVAL } from 'shared/time';
import { TimeConfigType, WorkTimeBreaksType, WorkTimeType } from 'shared/types';

import { getNearestDateByInterval } from '../../helpers';
import getDateFromWorkTime from '../../helpers/getDateFromWorkTime';

type Props = {
	workTimeAll?: WorkTimeType[];
	timeConfig?: TimeConfigType;
	date: Date | null | undefined;
	asapTimeDelayInMinutes?: number;
	ownFirstTime?: Date;
	ownDateFromWorkTime?: { [key: string]: Date };
};

/**
 * Get Order time options, based on selected day in location working hours and order delay.
 * @asapTimeDelayInMinutes: When ASAP is selected, determinate how many minutes should be added for user to fill from and make an order.
 * */
const getTimeOptions = ({
	date,
	timeConfig,
	workTimeAll,
	asapTimeDelayInMinutes = 0,
	ownDateFromWorkTime,
	ownFirstTime,
}: Props): WideSelectOption[] => {
	const minutesInterval = timeConfig?.interval ?? DEFAULT_INTERVAL;
	const workDay = dayjs(date);
	const workTime = workTimeAll?.find((w) => w.dayOfWeek === workDay.day());
	const isToday = workDay.isToday();

	if (!workTime) return [];

	const { tillDelayed, fromDelayed } =
		ownDateFromWorkTime ??
		getDateFromWorkTime({
			workTime,
			date: workDay.toDate(),
			delay: timeConfig?.delay,
		});
	const nowWithDelay = dayjs().add(timeConfig?.delay ?? 0, 'minutes');
	const isTodayDayAvailable = isToday && dayjs(fromDelayed).isBefore(nowWithDelay);

	const firstTime = _getFirstTime({
		isTodayDayAvailable,
		nowWithDelay: nowWithDelay.toDate(),
		ownFirstTime,
		fromDelayed,
		minutesInterval,
		ownDateFromWorkTime: !!ownDateFromWorkTime,
	});

	const shouldAsapBeDelayed = _shouldAsapBeDelayed({
		date: nowWithDelay,
		asapTimeDelayInMinutes,
		firstTimeInList: firstTime,
		isTodayDayAvailable,
	});

	const { breaks } = workTime;
	const options: CustomSelectOption[] = [];
	const diffInMinutes = Math.abs(firstTime.diff(tillDelayed, 'minutes'));
	let countMinutes = 0;

	for (let i = 0; countMinutes <= diffInMinutes; i++) {
		const day = firstTime.add(
			countMinutes + (countMinutes === 0 && shouldAsapBeDelayed ? asapTimeDelayInMinutes : 0),
			'minutes'
		);
		const isAvailable = _getIsTimeAvailable({ breaks, date: day });
		if (isAvailable) {
			options.push({
				label: day.format('HH:mm'),
				value: day.format('YYYY-MM-DD HH:mm'),
			});
		}

		countMinutes += minutesInterval;
	}

	return options as WideSelectOption[];
};

const _getFirstTime = ({
	ownFirstTime,
	ownDateFromWorkTime,
	fromDelayed,
	minutesInterval,
	nowWithDelay,
	isTodayDayAvailable,
}: {
	ownFirstTime?: Date;
	minutesInterval: number;
	ownDateFromWorkTime?: boolean;
	fromDelayed: Date;
	nowWithDelay: Date;
	isTodayDayAvailable?: boolean;
}) => {
	let datetime = isTodayDayAvailable ? nowWithDelay : fromDelayed;

	if (ownFirstTime) {
		datetime = ownFirstTime;
	} else if (ownDateFromWorkTime) {
		datetime = fromDelayed;
	}

	return dayjs(
		getNearestDateByInterval({
			date: datetime,
			minutesInterval,
		})
	);
};

/**
 * check if asapTimeDelayInMinutes should be added to first time option,
 * check if there is less time left than minutes that should be added.
 * */
const _shouldAsapBeDelayed = ({
	asapTimeDelayInMinutes,
	date,
	firstTimeInList,
	isTodayDayAvailable,
}: {
	date: Dayjs;
	asapTimeDelayInMinutes: number;
	firstTimeInList: Dayjs;
	isTodayDayAvailable: boolean;
}) => {
	if (asapTimeDelayInMinutes === 0 || !isTodayDayAvailable) return false;

	return firstTimeInList.diff(date, 'minutes') < asapTimeDelayInMinutes;
};

/**
 * Get if time is available due to breaks
 * */
const _getIsTimeAvailable = ({ breaks, date }: { breaks?: WorkTimeBreaksType[]; date: Dayjs }) => {
	if (!breaks) return true;

	const dateToCheck = date.toDate();

	for (const i of breaks) {
		const breakRange = getDateFromWorkTime({ workTime: i, date: dateToCheck });
		if (dateToCheck >= breakRange.from && dateToCheck <= breakRange.till) {
			return false;
		}
	}

	return true;
};

export default getTimeOptions;
